import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import _ from 'lodash';
import { Select } from 'antd';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';

const propTypes = {
  form: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  extra: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  help: PropTypes.string,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  fontSize: PropTypes.string,
  options: PropTypes.array,
  optionGroups: PropTypes.object,
  showIdAfterName: PropTypes.bool,
  isRequired: PropTypes.bool,
  validator: PropTypes.func,
  onSelect: PropTypes.func,
};

const defaultProps = {
  initialValue: undefined,
  placeholder: '',
  disabled: false,
  allowClear: true,
  showIdAfterName: false,
  isRequired: false,
};

@autobind
class FormSelect extends BaseComponent {
  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps) {
    const { form, name, options } = this.props;

    // If the options changed, we may need to clear the current selection.
    if (!_.isEqual(options, prevProps.options)) {
      const currentVal = form.getFieldValue(name);
      if (currentVal === null || currentVal === undefined ||
        !options || !options.map(this.createKey).includes(currentVal)
      ) {
        form.setFieldsValue({
          [name]: undefined,
        });
      }
    }
  }

  createKey(option) {
    return _.isstring(option) ? option : (option.value || option.id);
  }

  createText(option) {
    if (_.isstring(option)) {
      return option;
    } else if (option.text) {
      return option.text;
    } else if (option.name) { // name-id pair.
      return option.name + (this.props.showIdAfterName ? ` (${option.id})` : '');
    } else {
      return '';
    }
  }

  render() {
    const {
      form,
      name,
      label,
      extra,
      initialValue,
      placeholder,
      help,
      disabled,
      allowClear,
      fontSize,
      options,
      optionGroups,
      isRequired,
      validator,
      onSelect,
      ...layoutProps
    } = this.props;

    const { getFieldDecorator } = form;

    const requiredRule = { required: isRequired, message: 'This field should not be empty.' };
    const rules = [requiredRule, validator].filter(Boolean);

    return (
      <styledComp.AntdFormItem
        {...layoutProps}
        label={label}
        extra={extra}
        help={help}
      >
        {getFieldDecorator(name, {
          rules,
          // getLong('xxxId') from backend may return null, but we never want to display
          // null which renders in Select as an empty string. Instead, we want to set it to
          // undefined which will render the 'Select...' placeholder correctly.
          initialValue: initialValue === null ? undefined : initialValue,
        })(
          <Select
            style={{
              width: '100%',
              fontSize,
            }}
            placeholder={placeholder}
            disabled={disabled}
            allowClear={allowClear}
            showSearch
            optionFilterProp="text"
            onSelect={onSelect}
          >
            {options && options.map((option, index) => (
              <Select.Option
                key={this.createKey(option)}
                value={this.createKey(option)}
                text={this.createText(option)} /* For filter only */
              >
                {this.createText(option)}
              </Select.Option>
            ))}

            {optionGroups && Object.entries(optionGroups).map(([group, grpOptions], index) => (
              <Select.OptGroup
                key={group}
                label={group}
              >
                {grpOptions && grpOptions.map((option, index2) => (
                  <Select.Option
                    key={this.createKey(option)}
                    value={this.createKey(option)}
                    text={this.createText(option)} /* For filter only */
                  >
                    {this.createText(option)}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>,
        )}
      </styledComp.AntdFormItem>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
FormSelect.propTypes = propTypes;
FormSelect.defaultProps = defaultProps;

export default FormSelect;
